import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useCreateLead } from "@/hooks/useLeads";
import { useTurnstile } from "@/hooks/useTurnstile";
import {
  captchaState,
  formElapsedMs,
  HONEYPOT_INPUT_PROPS,
  leadSubmitErrorMessage,
} from "@/lib/leadIntake";
import { toast } from "sonner";
import { z } from "zod";
import { cn } from "@/lib/utils";

const schema = z.object({
  name: z.string().trim().min(2, "Nome deve ter pelo menos 2 caracteres").max(100),
  whatsapp: z.string().trim().min(8, "WhatsApp inválido").max(20),
  email: z.string().trim().email("E-mail inválido").max(255),
});

type FormData = z.infer<typeof schema>;

interface AutomacaoCTAFormProps {
  pillarSlug: "crm" | "atendimento" | "erp";
  pillarTag: string;
}

const inputClass =
  "h-[46px] w-full rounded-xl border border-white/[.16] bg-white/[.06] px-[15px] text-sm text-white placeholder:text-white/60 outline-none focus:border-white/40";

const AutomacaoCTAForm = ({ pillarSlug, pillarTag }: AutomacaoCTAFormProps) => {
  const [formData, setFormData] = useState<FormData>({ name: "", whatsapp: "", email: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  // Time-trap: submit-lead quarantines forms sent faster than a human types.
  const mountedAt = useRef(Date.now());
  const createLead = useCreateLead();
  const turnstile = useTurnstile();

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormData, string>> = {};
      result.error.errors.forEach((err) => {
        fieldErrors[err.path[0] as keyof FormData] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const captcha = captchaState(turnstile);
    if (captcha !== "ready") {
      toast.error(
        captcha === "failed"
          ? "A verificação de segurança não carregou. Recarregue a página ou chame no WhatsApp."
          : "Confirme a verificação de segurança.",
      );
      return;
    }

    try {
      await createLead.mutateAsync({
        name: result.data.name,
        email: result.data.email,
        phone: result.data.whatsapp,
        message: `Diagnóstico gratuito — prioridade: ${pillarTag}`,
        project_type: `automacao_${pillarSlug}`,
        source: "landing_automacao_empresarial",
        captchaToken: turnstile.token ?? undefined,
        honeypot,
        elapsedMs: formElapsedMs(mountedAt.current, Date.now()),
      });
      setSubmitted(true);
      toast.success("Recebido! Vou entrar em contato em breve.");
    } catch (err) {
      turnstile.reset();
      toast.error(
        leadSubmitErrorMessage(
          err instanceof Error ? err.message : null,
          "Erro ao enviar. Tente novamente.",
        ),
      );
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col gap-2 text-white">
        <div className="text-[17px] font-bold">Recebido, {formData.name}!</div>
        <p className="text-sm leading-[1.6] text-white/70">
          Te chamo no WhatsApp em até 1 dia útil pra marcar o diagnóstico.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="relative flex flex-col gap-[11px]" noValidate>
      {/* Honeypot: off-screen (not display:none, which bots skip). Humans never fill it. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor={`automacao-website-${pillarSlug}`}>Site</label>
        <input
          id={`automacao-website-${pillarSlug}`}
          {...HONEYPOT_INPUT_PROPS}
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <div>
        <input
          value={formData.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="Seu nome"
          disabled={createLead.isPending}
          aria-label="Seu nome"
          className={cn(inputClass, errors.name && "border-bad")}
        />
        {errors.name && <p className="mt-1 text-xs text-bad">{errors.name}</p>}
      </div>
      <div>
        <input
          value={formData.whatsapp}
          onChange={(e) => handleChange("whatsapp", e.target.value)}
          placeholder="WhatsApp"
          disabled={createLead.isPending}
          aria-label="WhatsApp"
          inputMode="tel"
          className={cn(inputClass, errors.whatsapp && "border-bad")}
        />
        {errors.whatsapp && <p className="mt-1 text-xs text-bad">{errors.whatsapp}</p>}
      </div>
      <div>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          placeholder="E-mail"
          disabled={createLead.isPending}
          aria-label="E-mail"
          className={cn(inputClass, errors.email && "border-bad")}
        />
        {errors.email && <p className="mt-1 text-xs text-bad">{errors.email}</p>}
      </div>
      <button
        type="submit"
        disabled={createLead.isPending}
        className="flex h-12 w-full items-center justify-center rounded-xl bg-primary text-[14.5px] font-bold text-primary-foreground transition-[filter] hover:brightness-95 disabled:opacity-70"
      >
        {createLead.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          "Agendar diagnóstico"
        )}
      </button>
      <div className="flex flex-col gap-1">
        <div ref={turnstile.containerRef} />
        {turnstile.failed ? (
          <p className="text-xs text-white/80">
            A verificação de segurança não carregou. Recarregue a página ou chame no WhatsApp.
          </p>
        ) : (
          turnstile.isReady &&
          !turnstile.token && <p className="text-xs text-white/60">Confirme que você não é robô.</p>
        )}
      </div>
      <span className="text-center text-[11.5px] text-white/60">Sem spam. Só eu respondendo.</span>
    </form>
  );
};

export default AutomacaoCTAForm;
